import { Icon } from '@iconify/react'
import { useMemo, useState } from 'react'
import {
  filterSalesByRange,
  filterSalesBySearch,
  formatCurrency,
  formatDateTime,
  formatNumber,
  sortSales,
  type SalesSortKey,
} from '@/lib/campaign-dashboard'
import type { CampaignSalesResponse } from '@/types/campaign-report'
import { ConversionPageLink } from '@/components/Tracking'
import styles from './CampaignDashboard.module.css'

interface CampaignSalesTableProps {
  data: CampaignSalesResponse | null
  isLoading: boolean
  error: string | null
  onRetry: () => void
  dateFrom: string
  dateTo: string
  page: number
  onPageChange: (page: number) => void
}

function SortIndicator({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  if (!active) return <Icon icon="mdi:swap-vertical" />
  return <Icon icon={direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} />
}

export function CampaignSalesTable({
  data,
  isLoading,
  error,
  onRetry,
  dateFrom,
  dateTo,
  page,
  onPageChange,
}: CampaignSalesTableProps) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SalesSortKey>('paid_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const filteredRows = useMemo(() => {
    if (!data) return []
    const ranged = filterSalesByRange(data.sales, dateFrom, dateTo)
    const searched = filterSalesBySearch(ranged, search)
    return sortSales(searched, sortKey, sortDirection)
  }, [data, dateFrom, dateTo, search, sortDirection, sortKey])

  const highlights = useMemo(() => {
    const topSale = [...filteredRows].sort((left, right) => right.amount - left.amount)[0]
    const averageAmount =
      filteredRows.length > 0
        ? filteredRows.reduce((acc, sale) => acc + sale.amount, 0) / filteredRows.length
        : 0
    const averageQuantity =
      filteredRows.length > 0
        ? filteredRows.reduce((acc, sale) => acc + sale.quantity, 0) / filteredRows.length
        : 0

    return { topSale, averageAmount, averageQuantity }
  }, [filteredRows])

  const pagination = data?.pagination

  const toggleSort = (key: SalesSortKey) => {
    if (key === sortKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setSortDirection('desc')
  }

  return (
    <section className={styles.tableCard}>
      <div className={styles.tableHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Vendas</h2>
          <p className={styles.sectionSubtitle}>
            Pagina retornada pelo backend com filtro visual local sobre os itens carregados.
          </p>
        </div>
      </div>

      <div className={styles.tableToolbar}>
        <div className={styles.tableHighlights}>
          <span className={styles.badgeWarning}>
            <Icon icon="mdi:filter-outline" />
            Filtro local nesta pagina
          </span>
          <span className={styles.badgeInfo}>
            <Icon icon="mdi:cash-plus" />
            Maior venda:{' '}
            {highlights.topSale
              ? `${formatCurrency(highlights.topSale.amount)} (#${highlights.topSale.payment_id})`
              : 'Sem dados'}
          </span>
          <span className={styles.badgeInfo}>
            <Icon icon="mdi:scale-balance" />
            Media valor: {formatCurrency(highlights.averageAmount)}
          </span>
          <span className={styles.badgeInfo}>
            <Icon icon="mdi:counter" />
            Media quantidade: {formatNumber(highlights.averageQuantity)}
          </span>
        </div>

        <div className={styles.tableSearchWrap}>
          <input
            type="search"
            className={`${styles.searchInput} ${styles.tableSearch}`}
            placeholder="Buscar por comprador, email ou ID da venda"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loadingCard}>
          <div className={styles.skeletonGrid}>
            <div className={styles.skeletonBlock} />
          </div>
        </div>
      ) : error ? (
        <div className={styles.errorState} role="alert">
          <span className={styles.stateIcon}>
            <Icon icon="mdi:alert-circle-outline" />
          </span>
          <h3 className={styles.stateTitle}>Erro ao carregar vendas</h3>
          <p className={styles.stateText}>{error}</p>
          <button type="button" className={styles.actionButton} onClick={onRetry}>
            <Icon icon="mdi:refresh" />
            Tentar novamente
          </button>
        </div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('payment_id')}>
                      ID
                      <SortIndicator active={sortKey === 'payment_id'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('donor_name')}>
                      Comprador
                      <SortIndicator active={sortKey === 'donor_name'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>Email</th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('amount')}>
                      Valor
                      <SortIndicator active={sortKey === 'amount'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('quantity')}>
                      Quantidade
                      <SortIndicator active={sortKey === 'quantity'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>Valor por numero</th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('paid_at')}>
                      Pago em
                      <SortIndicator active={sortKey === 'paid_at'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className={styles.sortableHeader} onClick={() => toggleSort('created_at')}>
                      Criado em
                      <SortIndicator active={sortKey === 'created_at'} direction={sortDirection} />
                    </button>
                  </th>
                  <th>Página da compra</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className={styles.emptyState}>
                        <span className={styles.emptyIcon}>
                          <Icon icon="mdi:cart-off" />
                        </span>
                        <h3 className={styles.stateTitle}>Nenhuma venda visivel nesta pagina</h3>
                        <p className={styles.stateText}>
                          Ajuste a busca ou o periodo para visualizar as vendas da pagina atual.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((sale) => (
                    <tr key={sale.payment_id}>
                      <td><span className={styles.tableName}>#{sale.payment_id}</span></td>
                      <td><div className={styles.tableName}>{sale.donor_name || 'Comprador nao identificado'}</div></td>
                      <td><span className={styles.tableMuted}>{sale.email || '-'}</span></td>
                      <td>{formatCurrency(sale.amount)}</td>
                      <td>{formatNumber(sale.quantity)}</td>
                      <td>{sale.quantity > 0 ? formatCurrency(sale.amount / sale.quantity) : '-'}</td>
                      <td>{formatDateTime(sale.paid_at)}</td>
                      <td>{formatDateTime(sale.created_at)}</td>
                      <td>
                        <ConversionPageLink
                          display={sale.tracking?.conversion_page_url_display}
                          rawUrl={sale.tracking?.conversion_page_url}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.paginationButton}
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                <Icon icon="mdi:chevron-left" />
                Anterior
              </button>
              <span className={styles.paginationInfo}>
                Pagina {pagination.current_page} de {pagination.last_page} · Total: {formatNumber(pagination.total)}
              </span>
              <button
                type="button"
                className={styles.paginationButton}
                disabled={page >= pagination.last_page}
                onClick={() => onPageChange(page + 1)}
              >
                Proxima
                <Icon icon="mdi:chevron-right" />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
